/**
 * The VAT note shown beneath shop prices.
 *
 * Shop prices are GROSS — Stripe receives them with
 * `tax_behavior: 'inclusive'`, so the amount a customer pays is identical
 * in every country and only the tax share inside it differs. The note
 * therefore reads "incl. 19 % VAT", never "plus".
 *
 * RESOLVED DURING SSR, deliberately. A first draft loaded this in
 * `onMounted`, which meant the price rendered without its VAT line until
 * the JavaScript arrived — and never at all without it. That disclosure
 * is required beneath consumer prices (PAngV), so it has no business
 * depending on hydration. Safe to render server-side here because nothing
 * caches the HTML: the tenant zones are DNS-only at Cloudflare, Caddy is a
 * plain reverse proxy, and there are no cache routeRules.
 *
 * `useRequestFetch` rather than plain `$fetch`: on the server the latter
 * would call the endpoint WITHOUT the visitor's cookies or forwarded IP,
 * so the override cookie and the geo lookup would both silently miss and
 * every SSR render would fall through to the default country.
 *
 * One `useAsyncData` key for the whole page, so a grid of forty cards
 * shares a single resolve.
 */
export interface VatContextPayload {
    countries: string[]
    country: string
    rate: number
    formattedRate: string
    applicable: boolean
    source: 'manual' | 'order' | 'geoip' | 'language' | 'default'
}

export function useVatNote() {
    const requestFetch = useRequestFetch()

    const { data: ctx, refresh } = useAsyncData<VatContextPayload | null>(
        'shop-vat-context',
        async () => {
            try {
                return await requestFetch<VatContextPayload>('/api/shop/vat-context')
            } catch {
                // A missing note beats a wrong one, and both beat a broken
                // page — render nothing and carry on.
                return null
            }
        },
        { default: () => null }
    )

    /** Change the country the note is calculated from. */
    async function setCountry(country: string | null) {
        try {
            ctx.value = await $fetch<VatContextPayload>('/api/shop/vat-country', {
                method: 'POST',
                body: { country: country || '' }
            })
        } catch {
            await refresh()
        }
    }

    return { ctx, refresh, setCountry }
}
